import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { InternalAffairsApi } from '@/api/api.internal-affairs';
import { IcDotVert } from '@/assets/images';
import { Page, PageHeader } from '@/components';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import { ExportAsPDFModal } from '@/components/Modals/InternalAffairsModal/ExportAsPdf';
import { SendEmailModal } from '@/components/Modals/InternalAffairsModal/SendEmail';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/TailwindComponents';
import { withLoader, WithLoaderProps } from '@/hocs';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import { IIACase, IACaseType, IACaseStatus, IFile, FileOrigin } from '@/models';
import { uploadFiles } from '@/services/service.file';
import { ConfirmTypes, useToastStore } from '@/store';

import { internalAffairsViewNavTabs } from './data';
import { generateSingleCasePDF } from './ia.utils';

export type InternalAffairsViewFormValues = {
  caseType: IACaseType | '';
  status: IACaseStatus;
  caseDetails: string;
  investigationNotes: string;
  officerDetails: string;
  findingsRecommendations: string;
  assignedOfficers: string[];
  files: IFile[];
};

const InternalAffairsViewPage: FC<
  WithLoaderProps & WithUnsavedChangesWarningProps
> = ({ showLoader, hideLoader }) => {
  const { ViewInternalAffairsId } = useParams<{
    ViewInternalAffairsId: string;
  }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { navigateWithSearch, searchQuery } = useSearchPersist();
  const { updateToast } = useToastStore();

  // Derive active tab from URL path
  const pathParts = pathname.split('/');
  const activeTab = pathParts[pathParts.length - 1];

  const [caseData, setCaseData] = useState<IIACase | null>(null);

  const methods = useForm<InternalAffairsViewFormValues>({
    defaultValues: {
      caseType: '',
      status: IACaseStatus.OPEN,
      caseDetails: '',
      investigationNotes: '',
      officerDetails: '',
      findingsRecommendations: '',
      assignedOfficers: [],
      files: [],
    },
    mode: 'all',
  });

  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Redirect to overview tab if no tab is selected
  useEffect(() => {
    const validTabs = internalAffairsViewNavTabs.map((t) => t.path);
    if (!validTabs.includes(activeTab)) {
      navigate(
        { pathname: 'overview', search: searchQuery.replace('?', '') },
        { replace: true },
      );
    }
  }, [activeTab, navigate]);

  useEffect(() => {
    const fetchCase = async () => {
      if (!ViewInternalAffairsId) return;

      showLoader();

      try {
        const response = await InternalAffairsApi.getOne(ViewInternalAffairsId);
        const data = response.data;
        setCaseData(data);

        const formData: InternalAffairsViewFormValues = {
          caseType: data.caseType,
          status: data.status,
          caseDetails: data.caseDetails || '',
          investigationNotes: data.investigationNotes || '',
          officerDetails: data.officerDetails || '',
          findingsRecommendations: data.findingsAndRecommendations || '',
          assignedOfficers:
            data.assignedOfficers?.map((o) =>
              typeof o === 'string' ? o : o._id,
            ) || [],
          files: data.files || [],
        };

        methods.reset(formData);
      } catch (error) {
        console.error('Error fetching case:', error);
      } finally {
        hideLoader();
      }
    };

    fetchCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ViewInternalAffairsId]);

  const handleEdit = () => {
    navigateWithSearch(
      `/tools/internal-affairs/edit/${ViewInternalAffairsId}/overview`,
    );
  };

  const handleTabChange = (tabPath: string) => {
    navigate(
      { pathname: tabPath, search: searchQuery.replace('?', '') },
      { replace: true },
    );
  };

  const handleExport = () => {
    setIsExportModalOpen(true);
  };

  const handleEmail = () => {
    setIsSendEmailModalOpen(true);
  };

  return (
    <Page title="View Internal Affairs Case">
      <PageHeader
        title="View Internal Affairs Case"
        onBack={() => navigateWithSearch('/tools/internal-affairs')}
      >
        <div className="flex gap-4">
          <Button
            type="button"
            variant="default"
            className="rounded-md px-[20px]"
            onClick={handleEdit}
          >
            Edit
          </Button>

          {ViewInternalAffairsId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer bg-white border border-grey-20 rounded-md px-4 py-3">
                  <IcDotVert />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleExport}
                  className="cursor-pointer font-medium"
                >
                  Export
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleEmail}
                  className="cursor-pointer font-medium"
                >
                  Email
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </PageHeader>

      <FormProvider {...methods}>
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="bg-transparent h-auto p-0 border-b !border-[#E7EAEC] dark:!border-[#4B5563] rounded-none w-full justify-start">
            {internalAffairsViewNavTabs.map((tab) => (
              <TabsTrigger
                key={tab.path}
                value={tab.path}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE3E5C] data-[state=active]:bg-transparent data-[state=active]:shadow-none mr-2 py-3"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Outlet context={{ caseData, setCaseData, showLoader, hideLoader }} />
      </FormProvider>

      <SendEmailModal
        open={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        loading={emailSending}
        onConfirm={async (emailData) => {
          if (!ViewInternalAffairsId) return;
          setEmailSending(true);
          showLoader();
          try {
            const pdfBlob = await generateSingleCasePDF(
              ViewInternalAffairsId,
              'blob',
              {
                recipient: emailData.recipient,
                reason: emailData.reason,
              },
            );
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
              console.error('Failed to generate PDF for email');
              return;
            }

            const fileName = `${
              caseData?.caseId || 'IA_Case'
            } - Emailed on ${new Date().toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}.pdf`;
            const file = new File([pdfBlob], fileName, {
              type: 'application/pdf',
            });

            const uploadedFiles = await uploadFiles([
              {
                name: file.name,
                size: file.size,
                mimeType: file.type,
                origin: FileOrigin.EMAIL,
                file,
              },
            ]);

            if (uploadedFiles?.length > 0) {
              const uploadedFile = uploadedFiles[0];
              await InternalAffairsApi.emailFiles(ViewInternalAffairsId, {
                emailAddress: emailData.recipientEmail,
                subject: 'Internal Affairs Report',
                emailBody: emailData.emailBody,
                files: [{ ...uploadedFile, uploadedBy: undefined }],
              });

              updateToast({
                open: true,
                message: 'Email sent successfully!',
                type: ConfirmTypes.SUCCESS,
              });
            }

            setIsSendEmailModalOpen(false);
          } catch (error: any) {
            console.error('Error sending email with PDF:', error);
            updateToast({
              open: true,
              message: error?.message || 'Failed to send email',
              type: ConfirmTypes.ERROR,
            });
          } finally {
            setEmailSending(false);
            hideLoader();
          }
        }}
        caseData={
          caseData
            ? {
                caseId: caseData.caseId,
                caseType: caseData.caseType,
                status: caseData.status,
                createdBy:
                  typeof caseData.createdBy === 'string'
                    ? caseData.createdBy
                    : (caseData.createdBy as any)?.fullName || 'Unknown',
              }
            : null
        }
      />

      <ExportAsPDFModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onConfirm={async (exportData) => {
          if (ViewInternalAffairsId) {
            await generateSingleCasePDF(
              ViewInternalAffairsId,
              'download',
              exportData,
            );
          }
          setIsExportModalOpen(false);
        }}
        caseData={
          caseData
            ? {
                caseId: caseData.caseId,
                caseType: caseData.caseType,
                status: caseData.status,
                createdBy:
                  typeof caseData.createdBy === 'string'
                    ? caseData.createdBy
                    : (caseData.createdBy as any)?.fullName || 'Unknown',
              }
            : null
        }
      />
    </Page>
  );
};

export default withLoader(withUnsavedChangesWarning(InternalAffairsViewPage));
